import React, { useState, useRef, useMemo } from "react";
import Button from "@mui/material/Button";
import MGridForm from "components/Shared/MGridForm";
import { DATA_TYPE_STRING, DATA_TYPE_CHECKBOX, DATA_TYPE_NUMBER, DATA_TYPE_DROPDOWN2 } from "typeConstants/dataTypes";
import { CustomerRCTCalculationSettingsDto } from "interfaces/Mueller.Business.Models";


interface CustomerRCTCalcSettingsFormProps {
  customerNumber: number;
  settings: CustomerRCTCalculationSettingsDto;
  onSave: (settings: CustomerRCTCalculationSettingsDto) => void;
  onCancel?: () => void;
  onBack?: (settings: CustomerRCTCalculationSettingsDto) => void;
  canEdit: boolean;
  portalTargetClass: string;
}

const newSettings: CustomerRCTCalculationSettingsDto = {
  enginePath: null,
  dataPath: null,
  constructionType: null,
  generalAdjustmentMultiplier: null,
  depreciationMultiplier: null,
  detachedDepreciationMultiplier: null,
  laborType: null,
  excavationFlag: null,
  builtStandard: null,
  valueType: null,
  fitProcessCompany: null,
  fitRoutingTargetSystem: null,
  maximumWings: null,
  usePierFoundationHeightSplit: false,
  useLocalizedCosts: false,
  fractionalStoryHeightEnabled: false,
  storyNumbers: null,
  debrisRemovalFlag: null,
  debrisRemovalMaximum: null,
  debrixRemovalMultiplier: null,
};

const CustomerRCTCalcSettingsForm = (props: CustomerRCTCalcSettingsFormProps) => {
  const [editSettings, setEditSettings] = useState(props.settings ?? newSettings);
  const formControlMethods = useRef({});

  // MGrid form

  const onSettingsChange = updatedObject => setEditSettings({ ...updatedObject });

  const memoizedFormDefinition = useMemo(
    () => {
      const yesNoFlagOptions = [
        { label: "Yes", value: "Y"},
        { label: "No", value: "N"},
      ];
      const laborTypeOptions = [
        { label: "Default", value: "D"},
        { label: "Reconstruction", value: "U"},
        { label: "New Construction", value: "N"},
      ];
      const buildStandardOptions = [
        { label: "Always use standard type", value: "1"},
        { label: "Always use year based construction", value: "2"},
        { label: "Enable standard/pre-1940/pre-1930", value: "3"},
        { label: "Enable standard/vintage option", value: "4"},
        { label: "Enable only std or std/vintage option", value: "5"},
      ]
      const valueTypeOptions = [
      { label: "Non-Standardized Costs", value: 1},
      { label: "Standardized Costs", value: 2},
      ];
      const enabled = () => props.canEdit ? true : false;
      const formDef = [
        [{
          dataType: DATA_TYPE_STRING,
          label: "Engine Path",
          source: "enginePath",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_STRING,
          label: "Data Path",
          source: "dataPath",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_NUMBER,
          label: "Construction Type",
          source: "constructionType",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_NUMBER,
          label: "General Adjustment Multiplier %",
          source: "generalAdjustmentMultiplier",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_NUMBER,
          label: "Depreciation Multiplier",
          source: "depreciationMultiplier",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_NUMBER,
          label: "Detached Depreciation Multiplier",
          source: "detachedDepreciationMultiplier",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_DROPDOWN2,
          label: "Labor Type",
          source: "laborType",
          enabled: enabled,
          dataTypeOptions: { options: laborTypeOptions },
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_DROPDOWN2,
          label: "Excavation Flag",
          source: "excavationFlag",
          enabled: enabled,
          dataTypeOptions: { options: yesNoFlagOptions },
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_DROPDOWN2,
          label: "Built Standard",
          source: "builtStandard",
          enabled: enabled,
          dataTypeOptions: { options: buildStandardOptions },
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_DROPDOWN2,
          label: "Value Type",
          source: "valueType",
          enabled: enabled,
          dataTypeOptions: { options: valueTypeOptions },
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_DROPDOWN2,
          label: "Debris Removal",
          source: "debrisRemovalFlag",
          enabled: enabled,
          dataTypeOptions: { options: [...yesNoFlagOptions] },
          hasValidationError: null,
        }],
        [{
          dataType: DATA_TYPE_STRING,
          label: "Debris Removal Only In States",
          source: "debrisRemovalOnlyInStates",
          enabled: enabled,
          hasValidationError: null,
        }],
        [{
          dataType: DATA_TYPE_NUMBER,
          label: "Debris Multiplier",
          source: "debrisRemovalMultiplier",
          enabled: enabled,
          hasValidationError: null,
        }],
        [{
          dataType: DATA_TYPE_NUMBER,
          label: "Debris Removal Max",
          source: "debrisRemovalMaximum",
          enabled: enabled,
          hasValidationError: null,
        }],
        [{
          dataType: DATA_TYPE_STRING,
          label: "FIT Process Company",
          source: "fitProcessCompany",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_STRING,
          label: "FIT RoutingTargetSystem",
          source: "fitRoutingTargetSystem",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_NUMBER,
          label: "Maximum Wings",
          source: "maximumWings",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_CHECKBOX,
          label: "Use Pier Foundation Height Split",
          source: "usePierFoundationHeightSplit",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_CHECKBOX,
          label: "Use Localized Costs",
          source: "useLocalizedCosts",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
        [{
          dataType: DATA_TYPE_CHECKBOX,
          label: "Fractional Story Height Enabled",
          source: "fractionalStoryHeightEnabled",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null
        }],
      ];
      return formDef;
    }, [props.canEdit]);

  // Buttons

  const wrappedOnSave = () => {
    //@ts-ignore
    if (formControlMethods.current.isFormValid()) {
      props.onSave(editSettings);
    }
  };

  const cancelButtonLabel = !props.canEdit
    ? "Close"
    : props.onBack
      ? "Back"
      : "Close";

  const wrappedOnCancel = () => {
    props.onBack
      ? props.onBack(editSettings)
      : props.onCancel();
  };

  return (
    <div>
      <MGridForm
        width={400}
        data={editSettings}
        onChange={onSettingsChange}
        formDefinition={memoizedFormDefinition}
        portalTarget={() => document.querySelector(props.portalTargetClass)}
        getControlMethods={cm => { formControlMethods.current = cm; }}
      />
      <div className="button-row-margin">
        <Button
          onClick={wrappedOnCancel}
        >{cancelButtonLabel}</Button>
        {props.canEdit && <Button
          onClick={wrappedOnSave}
        >Save</Button>}
      </div>
    </div>
  );
};

export default CustomerRCTCalcSettingsForm;
